import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { usePageTitle } from "@/hooks/usePageTitle";

const RIPPLE_LIGHT = "https://customer-assets.emergentagent.com/job_jci-content-platform/artifacts/xmrja44a_Ripples.png";

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

function isHtml(str) {
  return str && (str.trim().startsWith('<') || /<[a-z][\s\S]*>/i.test(str));
}

export default function ContentPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/pages/by-slug/${slug}`)
      .then(res => { setPage(res.data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  usePageTitle(page?.title);

  if (loading) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl py-24">
        <Skeleton className="h-10 w-2/3 mb-6" />
        <Skeleton className="h-4 w-full mb-3" />
        <Skeleton className="h-4 w-5/6 mb-3" />
        <Skeleton className="h-4 w-4/6" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-not-found">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-jci-black/10 mb-4">404</h1>
          <h2 className="text-2xl font-bold text-jci-black mb-2">Page Not Found</h2>
          <p className="text-slate-500">This page content has not been added yet.</p>
        </div>
      </div>
    );
  }

  // Collage images
  let collageImages = [];
  if (page.collage_images) {
    if (typeof page.collage_images === "string") {
      try { 
        collageImages = JSON.parse(page.collage_images); 
      } catch { 
        collageImages = page.collage_images.split(',').map(img => img.trim()).filter(img => img);
      }
    } else if (Array.isArray(page.collage_images)) {
      collageImages = page.collage_images;
    }
  }

  // Portrait hero for presidents-corner
  const isPortraitHero = slug === "presidents-corner";

  // Optional YouTube video
  const ytId = getYouTubeId(page.video_url);

  return (
    <div className="relative overflow-hidden" data-testid="content-page">
      <img src={RIPPLE_LIGHT} alt="" className="ripple-bg -right-40 -top-20 w-[500px]" />

      {/* Page Header */}
      <div className="bg-jci-black relative">
        <div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight" data-testid="content-page-title">{page.title}</h1>
          </div>
          <div className="h-1 bg-gradient-to-r from-jci-blue via-jci-teal to-jci-navy" />
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl py-16 relative z-10">
        {/* Standard hero image (non-portrait pages) */}
        {!isPortraitHero && page.image_url && (
          <img src={page.image_url} alt={page.title} className="w-full h-64 md:h-96 object-cover rounded-2xl mb-10 shadow-lg" data-testid="content-page-image" />
        )}

        {/* Presidents Corner: portrait image left of content */}
        {isPortraitHero && page.image_url && (
          <div className="mb-10 md:hidden" data-testid="content-page-portrait-mobile">
            <img src={page.image_url} alt={page.title} className="w-48 h-64 rounded-2xl shadow-xl object-cover object-top" />
          </div>
        )}

        {/* Page content - HTML or plain text */}
        <div data-testid="content-page-body">
          {isPortraitHero && page.image_url && (
            <img
              src={page.image_url}
              alt={page.title}
              className="hidden md:block float-left w-56 h-72 rounded-2xl shadow-xl object-cover object-top mr-8 mb-4"
              data-testid="content-page-portrait"
            />
          )}
          {isHtml(page.content) ? (
            <div className="prose prose-lg max-w-none prose-headings:text-jci-black prose-p:text-slate-600 prose-a:text-jci-blue" dangerouslySetInnerHTML={{ __html: page.content }} />
          ) : (
            <div className="prose prose-lg max-w-none">
              {page.content?.split('\n').map((p, i) => {
                if (p.startsWith('## ')) return <h2 key={i} className="text-2xl font-bold text-jci-black mt-8 mb-4">{p.replace('## ', '')}</h2>;
                if (p.startsWith('### ')) return <h3 key={i} className="text-xl font-bold text-jci-black mt-6 mb-3">{p.replace('### ', '')}</h3>;
                if (p.trim() === '') return <br key={i} />;
                return <p key={i} className="text-slate-600 leading-relaxed mb-4">{p}</p>;
              })}
            </div>
          )}
        </div>

        {/* Collage Images - inline below content, no title */}
        {collageImages.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4" data-testid="collage-images">
            {collageImages.map((img, idx) => (
              <button key={idx} onClick={() => setLightbox(img)} className="overflow-hidden rounded-xl group" data-testid={`collage-image-${idx}`}>
                <img src={img} alt="" className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105" />
              </button>
            ))}
          </div>
        )}

        {/* Optional YouTube Video */}
        {ytId && (
          <div className="mt-8 rounded-xl overflow-hidden shadow-md aspect-video" data-testid="page-video">
            <iframe src={`https://www.youtube.com/embed/${ytId}`} title="Video" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black/90 border-0">
          {lightbox && <img src={lightbox} alt="" className="w-full h-auto max-h-[80vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
